import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import LinuxDesktopArt from './LinuxDesktop.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Linux CLI Basics".
 *
 * Built to the same shape as the Windows one: it opens on a desktop,
 * the terminal has to be opened for real, and then nine steps run
 * inside it. Two operating systems answering the same five questions
 * with different words is the whole point of putting them side by side.
 *
 * Three ways in, all of them real on a GNOME machine: the Terminal in
 * the dock, Activities and a search, or Ctrl+Alt+T. The shortcut is
 * wired up because it is the one a learner will actually use, and
 * because nobody discovers it by looking.
 *
 * Everything the shell understands is read-only. It will not run
 * anything as root and nothing creates, deletes or changes a file -
 * which is a lesson as much as a limit, and /etc/shadow refusing to be
 * read says more about Linux than a paragraph would.
 */

const STORE_KEY = 'cybercub.lab.linux-cli';

const USER = 'aarav';
const HOST = 'cub-laptop';
const HOME = ['home', USER];

const BANNER = [
  'Welcome to CubOS 24.04 LTS (GNU/Linux 6.8.0-41-generic x86_64)',
  '',
  ' * Documentation:  https://help.example.invalid',
  '',
  'This shell is a practice one. Nothing you type here can change',
  'anything. Type help to see what it understands.',
  '',
];

/* ------------------------------------------------------------------ */
/* the machine                                                        */
/* ------------------------------------------------------------------ */

/*
 * A folder is an object; a file is a string of its contents. Nothing
 * needs a hidden flag the way the Windows one did - on Linux a name
 * beginning with a dot is the entire mechanism, which is exactly the
 * point step 3 is making.
 */
const DISK = {
  home: {
    aarav: {
      Desktop: {},
      Documents: {
        'homework.odt': 'Volcanoes: a report.\nBy Aarav, class 6B.',
        'notes.txt':
          'Remember: the wastebasket is not deletion.\nAsk Cub about the hidden folder.',
        '.private': {
          'cub-code.txt':
            'Well done for finding this.\n\nA dot at the front of a name is the whole of "hidden" on Linux.\nls -a shows it, and so does anything that reads the disk\ndirectly. It is a convention, not a lock.\n\nIf something really must stay private it has to be\nencrypted. Hiding is for tidiness.',
        },
      },
      Downloads: {
        'holiday.jpg': '(a picture - cat cannot show it)',
        'update.sh': '#!/bin/bash\necho "This script has never been run."',
      },
      '.bashrc': '# ~/.bashrc - runs every time a shell opens\nalias ll="ls -alF"',
      '.config': { gtk: {} },
    },
    meera: {},
  },
  etc: {
    passwd:
      'root:x:0:0:root:/root:/bin/bash\ndaemon:x:1:1:daemon:/usr/sbin:/usr/sbin/nologin\naarav:x:1000:1000:Aarav:/home/aarav:/bin/bash\nmeera:x:1001:1001:Meera:/home/meera:/bin/bash\n\nDespite the name there are no passwords in here - the x is where\none used to sit. They moved to /etc/shadow, which you cannot read.\nThe last field is the shell each account gets; nologin means that\naccount is not meant to sign in at all.',
    shadow: '',
    hostname: 'cub-laptop',
    ssh: { sshd_config: '# What the SSH server will and will not allow\nPermitRootLogin no\nPasswordAuthentication yes\nPort 22' },
  },
  var: {
    log: {
      'auth.log':
        'Sep 16 09:02:11 cub-laptop sshd[812]: Accepted password for aarav from 192.168.1.20\nSep 16 09:14:48 cub-laptop sudo:    aarav : TTY=pts/0 ; COMMAND=/usr/bin/apt update\nSep 16 03:41:02 cub-laptop sshd[655]: Failed password for root from 45.9.14.77\nSep 16 03:41:05 cub-laptop sshd[655]: Failed password for root from 45.9.14.77\nSep 16 03:41:09 cub-laptop sshd[657]: Failed password for admin from 45.9.14.77\n\nThose three at twenty to four are somebody trying passwords from\nan address nobody here has ever used. One failure is a typo. A\nrun of them, at that hour, is a machine having a go.',
    },
  },
  usr: { bin: { ls: '(a program)', nmap: '(a program)', bash: '(a program)' } },
  root: {},
  tmp: {},
};

/* Files and folders the account you are on is not allowed to read.
   /etc/shadow and root's home folder are the two every learner should
   meet, because being refused is the lesson. */
const PROTECTED = ['/root', '/etc/shadow'];

/* Anything not listed is -rw-r--r-- for a file, drwxr-xr-x for a
   folder. update.sh is here because it is the point of the lesson:
   it looks runnable and is not. */
const MODES = {
  '/home/aarav/Downloads/update.sh': '-rw-r--r--',
  '/usr/bin/ls': '-rwxr-xr-x',
  '/usr/bin/nmap': '-rwxr-xr-x',
  '/usr/bin/bash': '-rwxr-xr-x',
  '/etc/shadow': '-rw-r-----',
};

/* ------------------------------------------------------------------ */
/* path handling                                                      */
/* ------------------------------------------------------------------ */

const isFolder = (node) => node !== null && typeof node === 'object';

function nodeAt(parts) {
  let node = DISK;
  for (const part of parts) {
    if (!isFolder(node)) return null;
    if (!Object.prototype.hasOwnProperty.call(node, part)) return null;
    node = node[part];
  }
  return node;
}

/** The real path, always from the top of the tree. */
const abs = (parts) => (parts.length ? `/${parts.join('/')}` : '/');

/** What the prompt shows: the home folder collapses to a tilde. */
function pretty(parts) {
  if (parts[0] === 'home' && parts[1] === USER) {
    const rest = parts.slice(2);
    return rest.length ? `~/${rest.join('/')}` : '~';
  }
  return abs(parts);
}

/** Resolve whatever was typed against where the shell is standing. */
function resolve(cwd, raw) {
  const arg = raw.replace(/^["']|["']$/g, '');
  let parts;
  if (arg === '~' || arg.startsWith('~/')) parts = [...HOME, ...arg.slice(2).split('/')];
  else if (arg.startsWith('/')) parts = arg.split('/');
  else parts = [...cwd, ...arg.split('/')];

  const out = [];
  for (const p of parts.filter(Boolean)) {
    if (p === '.') continue;
    if (p === '..') out.pop();
    else out.push(p);
  }
  return out;
}

const isProtected = (parts) => PROTECTED.includes(abs(parts));

/** Every path under a folder, for find. */
function walk(parts, node, out = []) {
  if (!isFolder(node)) return out;
  for (const [name, child] of Object.entries(node)) {
    const here = [...parts, name];
    out.push(here);
    if (isFolder(child)) walk(here, child, out);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* the commands                                                       */
/* ------------------------------------------------------------------ */

const padStart = (s, n) => String(s).padStart(n, ' ');

function names(node, showHidden) {
  return Object.keys(node)
    .filter((n) => showHidden || !n.startsWith('.'))
    .sort((a, b) => a.localeCompare(b));
}

function listLong(parts, node, showHidden) {
  const list = names(node, showHidden);
  const lines = [`total ${list.length * 4}`];
  if (showHidden) lines.push('drwxr-xr-x  aarav aarav  4096 Sep 16 09:14 .', 'drwxr-xr-x  root  root   4096 Sep 12 18:02 ..');
  for (const name of list) {
    const child = node[name];
    const here = [...parts, name];
    const mode = MODES[abs(here)] ?? (isFolder(child) ? 'drwxr-xr-x' : '-rw-r--r--');
    const size = isFolder(child) ? 4096 : String(child).length;
    const owner = parts[0] === 'home' ? 'aarav aarav' : 'root  root ';
    lines.push(`${mode}  ${owner} ${padStart(size, 5)} Sep 16 09:14 ${name}`);
  }
  return lines;
}

const NET = [
  '1: lo: <LOOPBACK,UP,LOWER_UP> mtu 65536',
  '    inet 127.0.0.1/8 scope host lo',
  '2: wlp2s0: <BROADCAST,MULTICAST,UP,LOWER_UP> mtu 1500',
  '    link/ether 9c:b6:d0:1f:3a:77',
  '    inet 192.168.1.42/24 brd 192.168.1.255 scope global wlp2s0',
  '',
  '192.168.1.42 is this machine on the home network. The /24 says the',
  'first three numbers are the network, so anything from 192.168.1.1 to',
  '192.168.1.254 is a neighbour.',
];

const HELP = [
  'Commands you can use here:',
  '',
  '  pwd              print the folder you are in',
  '  ls               list this folder',
  '  ls -a            list it including hidden files (the dotted ones)',
  '  ls -l            list it with permissions, owner and size',
  '  cd <folder>      move into it   (cd .. up, cd on its own goes home)',
  '  find . -name x   search this folder and everything under it',
  '  tree             draw the folders as a shape',
  '  cat <file>       print what is inside a file',
  '  whoami           which account am I?',
  '  id               which account, and which groups it is in',
  '  hostname         what is this machine called?',
  '  uname -a         which kernel is it running?',
  '  ip a             what network is it on?',
  '  ps aux           what is running',
  '  ss -tulpn        what is listening on the network',
  '  which <name>     where a program actually lives',
  '  clear            clear the screen',
  '  exit             close the window',
  '',
  'Nothing here changes anything. It only ever looks.',
];

/* ------------------------------------------------------------------ */
/* the nine steps                                                     */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    id: 's1',
    title: 'Where am I?',
    ask: 'Every shell starts somewhere. Print where.',
    hint: 'pwd',
    done: (cmd) => /^pwd\s*$/i.test(cmd),
  },
  {
    id: 's2',
    title: "What's around me?",
    ask: 'List what is in this folder.',
    hint: 'ls',
    done: (cmd) => /^ls\s*$/.test(cmd),
  },
  {
    id: 's3',
    title: 'Are there hidden files?',
    ask: 'ls does not show everything here. Ask it to.',
    hint: 'ls -a',
    done: (cmd) => /^ls\s+-\w*a/.test(cmd),
  },
  {
    id: 's4',
    title: 'Moving around the filesystem',
    ask: 'Move into the Documents folder.',
    hint: 'cd Documents',
    done: (cmd, { cwd }) => /^cd\s+\S/.test(cmd) && cwd[cwd.length - 1] === 'Documents',
  },
  {
    id: 's5',
    title: 'Finding the file on the disk',
    ask: 'Somewhere under here is a file called cub-code.txt. Search for it without knowing where it is.',
    hint: 'find . -name cub-code.txt',
    done: (cmd) => /^find\b/.test(cmd) && /cub-code/.test(cmd),
  },
  {
    id: 's6',
    title: 'Navigate to the file',
    ask: 'The search told you the folder. Go there.',
    hint: 'cd .private',
    done: (cmd, { cwd }) => /^cd\s+\S/.test(cmd) && cwd[cwd.length - 1] === '.private',
  },
  {
    id: 's7',
    title: 'Read the file',
    ask: 'Print what is inside it.',
    hint: 'cat cub-code.txt',
    done: (cmd) => /^cat\s+.*cub-code\.txt/.test(cmd),
  },
  {
    id: 's8',
    title: 'Name of the computer',
    ask: 'What is this machine actually called?',
    hint: 'hostname',
    done: (cmd) => /^hostname\s*$/.test(cmd),
  },
  {
    id: 's9',
    title: 'How is this machine connected?',
    ask: 'Find its address on the network.',
    hint: 'ip a',
    done: (cmd) => /^(ip\s+a|ip\s+addr|ifconfig)\b/.test(cmd),
  },
];

/* What Activities shows before anything is typed. */
const PINNED = ['Firefox', 'Files', 'Text Editor', 'Software', 'Settings', 'Calculator'];

const WANTS_TERM = /^(term|terminal|gnome-terminal|console|shell|bash)/i;

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function LinuxCliLab() {
  const saved = useMemo(load, []);

  // 'closed' | 'open' | 'min' | 'max'
  const [win, setWin] = useState('closed');
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState('');

  const [cwd, setCwd] = useState(HOME);
  const [lines, setLines] = useState(BANNER);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [hPos, setHPos] = useState(-1);
  const [doneSteps, setDoneSteps] = useState(saved.done ?? []);
  const [showHint, setShowHint] = useState(false);

  const screenRef = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { document.title = 'Linux CLI Basics · CyberCub Lab'; }, []);
  useEffect(() => { save({ done: doneSteps }); }, [doneSteps]);

  useEffect(() => {
    const el = screenRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, win]);

  useEffect(() => { if (menu) searchRef.current?.focus(); }, [menu]);
  useEffect(() => {
    if (win === 'open' || win === 'max') inputRef.current?.focus();
  }, [win]);

  const showing = win === 'open' || win === 'max';
  const step = showing ? (STEPS.find((s) => !doneSteps.includes(s.id)) ?? null) : null;
  const allDone = doneSteps.length === STEPS.length;

  useFinished('/lab/linux-cli', allDone);

  const prompt = `${USER}@${HOST}:${pretty(cwd)}$ `;

  const openTerm = () => {
    setMenu(false);
    setSearch('');
    setShowHint(false);
    setWin((w) => (w === 'max' ? 'max' : 'open'));
  };

  const closeTerm = () => {
    setWin('closed');
    setLines(BANNER);
    setCwd(HOME);
    setInput('');
    setHistory([]);
    setHPos(-1);
  };

  // Ctrl+Alt+T is how anybody who uses Linux actually opens one, and
  // it is worth a learner meeting it here rather than never.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey && e.altKey && (e.key === 't' || e.key === 'T')) {
        e.preventDefault();
        openTerm();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const run = (raw) => {
    const cmd = raw.trim();
    const out = [`${prompt}${raw}`];
    let nextCwd = cwd;

    if (!cmd) {
      setLines((l) => [...l, ...out]);
      return;
    }

    const [verb, ...rest] = cmd.split(/\s+/);
    const flags = rest.filter((r) => r.startsWith('-')).join('');
    const args = rest.filter((r) => !r.startsWith('-'));

    if (verb === 'clear') {
      setLines([]);
      setInput('');
      return;
    }
    if (verb === 'exit' || verb === 'logout') {
      closeTerm();
      return;
    }

    const notFound = (what) => `${verb}: cannot access '${what}': No such file or directory`;

    if (verb === 'help') out.push(...HELP);
    else if (verb === 'pwd') out.push(abs(cwd));
    else if (verb === 'whoami') out.push(USER);
    else if (verb === 'id') out.push(`uid=1000(${USER}) gid=1000(${USER}) groups=1000(${USER}),27(sudo)`);
    else if (verb === 'hostname') out.push(HOST);
    else if (verb === 'uname') {
      out.push(
        flags.includes('a')
          ? `Linux ${HOST} 6.8.0-41-generic #41-Ubuntu SMP x86_64 GNU/Linux`
          : 'Linux',
      );
    } else if (verb === 'ip' || verb === 'ifconfig') out.push(...NET);
    else if (verb === 'ps') {
      out.push(
        'USER         PID %CPU %MEM COMMAND',
        'root           1  0.0  0.4 /sbin/init',
        'root         655  0.0  0.3 sshd: /usr/sbin/sshd -D',
        'aarav       1804  0.6  2.1 /usr/lib/firefox/firefox',
        'aarav       2210  0.0  0.1 bash',
        'aarav       2288  0.0  0.1 ps aux',
      );
    } else if (verb === 'ss' || verb === 'netstat') {
      out.push(
        'Netid State  Local Address:Port   Peer Address:Port  Process',
        'tcp   LISTEN 0.0.0.0:22           0.0.0.0:*          sshd',
        'tcp   LISTEN 127.0.0.1:631        0.0.0.0:*          cupsd',
        'tcp   ESTAB  192.168.1.42:51244   142.250.185.78:443 firefox',
        '',
        'LISTEN means something on this machine is waiting for a connection.',
        'Port 22 is SSH, and it is open to the whole network — that is a door',
        'worth asking about. 631 is the printer, and it is listening only to',
        '127.0.0.1, which means only this machine can reach it.',
      );
    } else if (verb === 'which') {
      const name = args[0] ?? '';
      out.push(nodeAt(['usr', 'bin', name]) ? `/usr/bin/${name}` : '');
    } else if (verb === 'cd') {
      const target = args.length ? resolve(cwd, args[0]) : HOME;
      if (isProtected(target)) out.push(`bash: cd: ${abs(target)}: Permission denied`);
      else if (isFolder(nodeAt(target))) nextCwd = target;
      else if (nodeAt(target) !== null) out.push(`bash: cd: ${args[0]}: Not a directory`);
      else out.push(`bash: cd: ${args[0]}: No such file or directory`);
    } else if (verb === 'ls') {
      const target = args.length ? resolve(cwd, args[0]) : cwd;
      const node = nodeAt(target);
      if (isProtected(target)) {
        out.push(`ls: cannot open directory '${abs(target)}': Permission denied`);
      } else if (node === null) out.push(notFound(args[0]));
      else if (!isFolder(node)) out.push(args[0]);
      else if (flags.includes('l')) out.push(...listLong(target, node, flags.includes('a')));
      else {
        const list = names(node, flags.includes('a'));
        out.push(list.length ? list.join('   ') : '');
      }
    } else if (verb === 'cat') {
      const target = resolve(cwd, args[0] ?? '');
      if (isProtected(target)) {
        out.push(
          `cat: ${abs(target)}: Permission denied`,
          '',
          'That refusal is the system working. /etc/shadow holds the hashed',
          'passwords, and only root may read it — which is why the account',
          'list in /etc/passwd can be world-readable without giving anything',
          'away.',
        );
      } else if (nodeAt(target) === null) out.push(`cat: ${args[0]}: No such file or directory`);
      else if (isFolder(nodeAt(target))) out.push(`cat: ${args[0]}: Is a directory`);
      else out.push(...String(nodeAt(target)).split('\n'));
    } else if (verb === 'find') {
      const where = args[0] && !args[0].startsWith('-') ? resolve(cwd, args[0]) : cwd;
      const nameIdx = rest.indexOf('-name');
      const term = nameIdx >= 0 ? (rest[nameIdx + 1] ?? '').replace(/["'*]/g, '') : '';
      const hits = walk(where, nodeAt(where))
        .filter((p) => !term || p[p.length - 1].includes(term))
        .map((p) => (args[0] === '.' || !args.length ? `./${p.slice(where.length).join('/')}` : abs(p)));
      out.push(...(hits.length ? hits : []));
    } else if (verb === 'tree') {
      const draw = (parts, node, depth) => {
        if (!isFolder(node) || depth > 2) return;
        for (const name of names(node, false)) {
          out.push(`${'   '.repeat(depth)}├── ${name}`);
          draw([...parts, name], node[name], depth + 1);
        }
      };
      out.push(abs(cwd));
      draw(cwd, nodeAt(cwd), 0);
    } else if (verb === 'sudo') {
      out.push(
        `[sudo] password for ${USER}:`,
        '',
        'This practice machine will not run anything as root, so there is no',
        'password to type. What matters is what you have just been asked for:',
        'sudo borrows root for one command, and typing your password in front',
        'of a command you did not expect hands over the whole machine.',
      );
    } else if (verb === './update.sh' || cmd === 'bash update.sh') {
      out.push(
        'bash: ./update.sh: Permission denied',
        '',
        'The name ends .sh, and it still will not run. Linux does not decide',
        'by the name — a file runs only when it is marked executable, and',
        'this one is not. Check it yourself with ls -l.',
      );
    } else {
      out.push(
        `${verb}: command not found`,
        '',
        'Type help to see what this shell understands.',
      );
    }

    out.push('');
    setLines((l) => [...l, ...out]);
    setCwd(nextCwd);
    setHistory((h) => [cmd, ...h].slice(0, 40));
    setHPos(-1);
    setShowHint(false);

    if (step && step.done(cmd, { cwd: nextCwd })) {
      setDoneSteps((d) => [...d, step.id]);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') {
      run(input);
      setInput('');
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const n = Math.min(hPos + 1, history.length - 1);
      if (n >= 0) { setHPos(n); setInput(history[n]); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const n = hPos - 1;
      setHPos(n);
      setInput(n >= 0 ? history[n] : '');
    }
  };

  const task = showing
    ? step && { title: step.title, ask: step.ask, hint: step.hint }
    : {
        title: win === 'min' ? 'Bring the window back' : 'Open a terminal',
        ask:
          win === 'min'
            ? 'It has not closed — it is minimised. Click Terminal on the dock again and it comes back.'
            : 'Click Terminal in the dock. Or open Activities, type terminal, and pick it — or press Ctrl+Alt+T, which is how most people do it.',
        hint: win === 'min' ? 'Click Terminal on the dock again' : 'Ctrl+Alt+T',
      };

  const tutorSay = (() => {
    if (!showing) {
      return win === 'min'
        ? 'The window is only minimised, not closed. Its icon on the dock still has the dot beside it - click that and it comes straight back, with everything you typed still in it. That dot is the whole of what a dock tells you.'
        : 'Before you can type a command you need somewhere to type it. The Terminal is pinned to the dock, Activities will find it, and Ctrl+Alt+T opens one from anywhere. On Linux this window is not a last resort — it is where most of the work happens.';
    }
    if (allDone) {
      return 'All nine, in a different language. Same five questions as the Windows prompt asked: who am I, where am I, what is here, what is this machine, who can it reach. Learning the second one is mostly noticing what moved.';
    }
    if (!step) return 'Type help to see everything this shell understands.';
    return `Step ${doneSteps.length + 1}. ${step.title}${/[?.!]$/.test(step.title) ? '' : '.'} ${step.ask} If you get stuck, press the hint button.`;
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Linux CLI Basics</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf4-linux-basics" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <section className="lab-section">
        <div className="place-head">
          <div>
            <h2>Open a terminal</h2>
            <p className="lab-lead">
              A Linux desktop, and a shell you have to open yourself. Then type a command, press
              Enter, read the answer. Nothing here changes anything — every command only ever looks.
            </p>
          </div>
          <span className="pill">{doneSteps.length} / {STEPS.length} steps</span>
        </div>

        <Tutor avatar="cub-sun" say={tutorSay} />

        <ol className="cli-chips" aria-label="Steps">
          {STEPS.map((s, i) => {
            const done = doneSteps.includes(s.id);
            const now = step?.id === s.id;
            return (
              <li key={s.id} className={`${done ? 'is-done' : ''} ${now ? 'is-now' : ''}`}>
                <span className="cli-step-n">{done ? '✓' : i + 1}</span>
                <span className="cli-chip-label">{s.title}</span>
              </li>
            );
          })}
        </ol>

        {task && (
          <div className={`cli-task ${showing ? '' : 'is-opening'}`}>
            <div>
              <strong>{task.title}</strong>
              <p>{task.ask}</p>
            </div>
            {showHint ? (
              <code className="cli-hint">{task.hint}</code>
            ) : (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowHint(true)}>
                Show me
              </button>
            )}
          </div>
        )}

        {allDone && showing && (
          <div className="cli-task is-done">
            <div>
              <strong>All nine done.</strong>
              <p>
                The shell stays open. Try <code>ls -l</code>, <code>cat /etc/passwd</code>,{' '}
                <code>cat /etc/shadow</code>, <code>cat /var/log/auth.log</code>,{' '}
                <code>ss -tulpn</code> or <code>id</code>.
              </p>
            </div>
          </div>
        )}

        {/* ---------------- the desktop ---------------- */}
        <div className="win-desktop cli-desk">
          <LinuxDesktopArt running={win !== 'closed'} />

          {/* Terminal in the dock. A dock takes one click, not two. */}
          <button
            type="button"
            className="win-spot cli-hot"
            style={{ left: '1.6%', top: '30.5%', width: '5.5%', height: '8.5%' }}
            onClick={() => (win === 'min' ? setWin('open') : openTerm())}
            aria-label={win === 'min' ? 'Restore Terminal from the dock' : 'Terminal, pinned to the dock'}
          />

          {/* Activities, top left */}
          <button
            type="button"
            className="win-spot cli-hot"
            style={{ left: '1.5%', top: '0.5%', width: '11%', height: '5%' }}
            onClick={() => setMenu((m) => !m)}
            aria-label="Activities"
            aria-expanded={menu}
          />

          {/* ---------------- Activities ---------------- */}
          {menu && (
            <div className="cli-start is-linux" role="dialog" aria-label="Activities">
              <input
                ref={searchRef}
                className="cli-start-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && WANTS_TERM.test(search.trim())) openTerm();
                  if (e.key === 'Escape') setMenu(false);
                }}
                placeholder="Type to search"
                spellCheck="false"
                autoCapitalize="none"
                autoCorrect="off"
                aria-label="Search this machine"
              />

              {search.trim() === '' ? (
                <>
                  <p className="cli-start-head">Frequent</p>
                  <div className="cli-start-grid">
                    {PINNED.map((p) => (
                      <span key={p} className="cli-start-tile">{p}</span>
                    ))}
                  </div>
                  <p className="cli-start-foot">
                    Type <code>terminal</code> to find it — or close this and press{' '}
                    <code>Ctrl+Alt+T</code>, which opens one from anywhere.
                  </p>
                </>
              ) : WANTS_TERM.test(search.trim()) ? (
                <>
                  <p className="cli-start-head">Applications</p>
                  <button type="button" className="cli-start-hit" onClick={openTerm}>
                    <span className="cli-tb-tile">{'>_'}</span>
                    <span className="cli-start-hit-text">
                      <strong>Terminal</strong>
                      <em>Application</em>
                    </span>
                  </button>
                </>
              ) : (
                <p className="cli-start-none">
                  No results for “{search.trim()}”. Try <code>terminal</code>.
                </p>
              )}
            </div>
          )}

          {/* ---------------- the Terminal window ---------------- */}
          {showing && (
            <div className={`cli-win is-linux ${win === 'max' ? 'is-max' : ''}`}>
              <div className="cli-bar">
                <span className="cli-bar-title is-centre">
                  {USER}@{HOST}: {pretty(cwd)}
                </span>
                <button type="button" className="cli-win-btn" onClick={() => setWin('min')} aria-label="Minimise">
                  &#8211;
                </button>
                <button
                  type="button"
                  className="cli-win-btn"
                  onClick={() => setWin((w) => (w === 'max' ? 'open' : 'max'))}
                  aria-label={win === 'max' ? 'Restore down' : 'Maximise'}
                >
                  &#9633;
                </button>
                <button type="button" className="cli-win-btn is-close" onClick={closeTerm} aria-label="Close">
                  &#10005;
                </button>
              </div>

              <div
                className="cli-screen is-linux"
                ref={screenRef}
                onClick={() => inputRef.current?.focus()}
                role="presentation"
              >
                {lines.map((l, i) => (
                  <div key={i} className="cli-line">{l || ' '}</div>
                ))}

                <div className="cli-line cli-entry">
                  <span className="cli-prompt">{prompt}</span>
                  <input
                    ref={inputRef}
                    className="cli-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    spellCheck="false"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    aria-label="Type a command"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {allDone && (
          <div className="lab-done">
            <div>
              <strong>Two machines, one set of questions.</strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                pwd is cd. ls is dir. cat is type. ip a is ipconfig. The words changed and the
                thinking did not — which is why the second operating system takes an afternoon and
                the first one took a term.
              </p>
            </div>
            <Link to="/learn/lesson/cf4-linux-basics" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
